import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (dismissed) return;
    if (e.clientY <= 10) {
      setVisible(true);
    }
  }, [dismissed]);

  useEffect(() => {
    const alreadySeen = sessionStorage.getItem('exitPopupDismissed');
    if (alreadySeen) {
      setDismissed(true);
      return;
    }
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [handleMouseLeave]);

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem('exitPopupDismissed', 'true');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !role) {
      setError('Please fill in all fields.');
      return;
    }
    setError('');
    setLoading(true);
    const body = new URLSearchParams();
    body.append('email', email);
    body.append('role', role);
    try {
      await fetch('https://readdy.ai/api/form/d6sa9tea739gopmli1jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFullForm = () => {
    dismiss();
    navigate('/waitlist');
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={dismiss}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#060E1E] border border-[#00FFD1]/25 rounded-2xl overflow-hidden shadow-2xl shadow-black/60 animate-[fadeSlideUp_0.35s_ease_forwards]">

        {/* HUD corner accents */}
        <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-[#00FFD1]/40 pointer-events-none" />
        <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-[#00FFD1]/40 pointer-events-none" />
        <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-[#00FFD1]/40 pointer-events-none" />
        <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-[#00FFD1]/40 pointer-events-none" />

        {/* Subtle grid bg */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="relative z-10 p-8">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#00FFD1]/10 rounded-full border border-[#00FFD1]/30">
                <i className="ri-check-line text-[#00FFD1] text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">
                We'll reach out the moment your early access spot opens. Blue skies ahead.
              </p>
              <p className="mt-4 text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">
                CLEARED FOR DEPARTURE ✓
              </p>
              <button
                onClick={dismiss}
                className="mt-6 px-6 py-2 rounded-lg border border-white/20 text-sm text-gray-300 hover:text-white hover:border-white/40 transition-all cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Badge */}
              <div className="flex justify-center mb-5">
                <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-4 py-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
                  <span className="text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">
                    Wait — Don't Go Yet
                  </span>
                </div>
              </div>

              {/* Heading */}
              <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-2 leading-tight">
                Secure Your Spot Before<br />
                <span className="text-[#00FFD1]">It's Gone</span>
              </h2>
              <p className="text-center text-gray-400 text-sm mb-6 leading-relaxed">
                340+ pilots are already on the waitlist. Join free and be first to fly with your AI copilot.
              </p>

              {/* Perks */}
              <div className="flex justify-center gap-4 mb-6">
                {[
                  { icon: 'ri-shield-check-line', label: 'Free during beta' },
                  { icon: 'ri-gift-line', label: 'Founding perks' },
                  { icon: 'ri-lock-line', label: 'No spam, ever' },
                ].map((p) => (
                  <div key={p.label} className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg border border-white/10">
                      <i className={`${p.icon} text-[#00FFD1] text-base`} />
                    </div>
                    <span className="text-[10px] text-gray-400 text-center whitespace-nowrap">{p.label}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form
                data-readdy-form
                id="exit-intent-waitlist"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="captain@example.com"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    I fly as a…
                  </label>
                  <select
                    name="role"
                    required
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                  >
                    <option value="" disabled>Select your role…</option>
                    <option value="Student Pilot">Student Pilot</option>
                    <option value="Private Pilot">Private Pilot (PPL)</option>
                    <option value="Instrument Rated">Instrument Rated (IFR)</option>
                    <option value="Commercial Pilot">Commercial Pilot (CPL)</option>
                    <option value="CFI / Flight Instructor">CFI / Flight Instructor</option>
                    <option value="ATP / Airline Pilot">ATP / Airline Pilot</option>
                    <option value="Aviation Enthusiast">Aviation Enthusiast</option>
                  </select>
                </div>

                {error && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line" /> {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00FFD1] hover:bg-[#00e6bc] disabled:opacity-60 text-[#060E1E] font-bold py-3.5 rounded-lg transition-all hover:scale-[1.02] shadow-lg shadow-[#00FFD1]/20 cursor-pointer whitespace-nowrap text-sm"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" /> Submitting…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line" /> Join the Waitlist — It's Free
                    </span>
                  )}
                </button>
              </form>

              {/* Footer links */}
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={dismiss}
                  className="text-xs text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
                >
                  No thanks, I'll pass
                </button>
                <button
                  onClick={handleFullForm}
                  className="text-xs text-[#00FFD1] hover:underline cursor-pointer"
                >
                  Full sign-up form →
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
}
